import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { useEffect, useRef } from "react";

import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import styled from "styled-components";

const DRAG_TYPE = "list-item";

const StyledDragDropContainer = styled.div`
  /* min-height: 50px; */
  &.is-over {
    background-color: #f0f8ff;
  }
`;

// Base item with dynamic styling based on state
const BaseItem = styled.div`
  background: white; /* Default background */
  cursor: grab;
  border-bottom: 1px solid #ccc;

  /* Style when a drag is happening over this item */
  &.is-dropping {
    background-color: #e0f2fe;
    border: 2px dashed #3498db;
  }

  /* Style when this item is being dragged */
  &.is-dragging {
    opacity: 0.4;
    cursor: grabbing;
  }

  svg,
  p {
    padding: 0;
  }
`;

const DraggableListItem = ({ item, index, children }) => {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const data = {
      id: item.id,
      index,
      type: DRAG_TYPE,
    };

    const unbindDraggable = draggable({
      element,
      getInitialData: () => data,
      data: data,
      onDragStart: () => {
        element.classList.add("is-dragging");
      },
      onDrop: () => {
        element.classList.remove("is-dragging");
      },
    });

    const unbindDropTarget = dropTargetForElements({
      element,
      getData: () => data,
      canDrop: (args) => {
        return (
          args.source.data.type === DRAG_TYPE && args.source.data.id !== item.id
        );
      },
      onDragEnter: () => {
        element.classList.add("is-dropping");
      },
      onDragLeave: () => {
        element.classList.remove("is-dropping");
      },
      onDrop: () => {
        element.classList.remove("is-dropping");
      },
    });

    return () => {
      unbindDraggable();
      unbindDropTarget();
    };
  }, [index, item.id]);

  return <BaseItem ref={ref}>{children}</BaseItem>;
};

export const DragDropContainer = ({ items, onReorder, renderItem }) => {
  const listRef = useRef(items);

  useEffect(() => {
    listRef.current = items;
  }, [items]);

  useEffect(() => {
    const unbindMonitor = monitorForElements({
      onDrop: (args) => {
        const { source, location } = args;

        const target = location.current.dropTargets[0];

        if (!target) {
          return;
        }

        const latestList = listRef.current;
        const sourceIndex = source.data.index;
        const targetIndex = target.data.index;

        if (sourceIndex === targetIndex) {
          return;
        }

        const newList = reorder({
          list: latestList,
          startIndex: sourceIndex,
          finishIndex: targetIndex,
        });

        if (onReorder) {
          onReorder(newList);
        }
      },
    });

    return () => {
      unbindMonitor();
    };
  }, [onReorder]);

  return (
    <StyledDragDropContainer>
      {items.map((item, index) => (
        <DraggableListItem key={item.id} item={item} index={index}>
          {renderItem(item, index)}
        </DraggableListItem>
      ))}
    </StyledDragDropContainer>
  );
};
